import { memo, useCallback } from 'react';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { editAndRelayoutText } from '../../state/actions/edit-and-relayout-text';
import { relayoutCaptions } from '../../state/actions/relayout-captions';
import { useWriteContext } from '../../utils/use-context';
import { NumberControlUpdateHandler } from './number-controls';
import { useTranslations } from 'next-intl';
import { FormatLineSpacing } from '@crabs-ai/web-icons';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { TextItem } from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';

const LineHeightControlsUnmemoized: React.FC<{
  lineHeight: number;
  itemId: string;
}> = ({ lineHeight, itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<TextItem | CaptionsItem, number>(
    itemId,
    (item) => item.lineHeight,
  );

  const onLineHeightChange: NumberControlUpdateHandler = useCallback(
    ({ num, commitToUndoStack }) => {
      // lineHeight가 유효한 양수인지 확인
      const validLineHeight = Math.max(0.1, Math.min(5, num));

      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type === 'text') {
              return editAndRelayoutText(i, () => {
                if (i.lineHeight === validLineHeight) {
                  return i;
                }

                return {
                  ...i,
                  lineHeight: validLineHeight,
                };
              });
            }

            if (i.type === 'captions') {
              if (i.lineHeight === validLineHeight) {
                return i;
              }

              const newItem = {
                ...i,
                lineHeight: validLineHeight,
              };

              return relayoutCaptions(newItem);
            }

            throw new Error(
              'Line height can only be changed for text and captions items',
            );
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  return (
    <div className="flex-1">
      <ValueInputField
        isShowLabel={true}
        label={t('Editor.inspector.text.line-height')}
        isShowLeadingNode={true}
        leadingNodeType="icon"
        leadingNodeValue={
          <FormatLineSpacing className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
        }
        setValue={onLineHeightChange}
        value={lineHeight}
        min={0.5}
        max={5}
        step={0.1}
        defaultValue={1.2}
        accessibilityLabel="Line height"
        hasMixedValue={isMixed}
        className="w-full"
      />
    </div>
  );
};

export const LineHeightControls = memo(LineHeightControlsUnmemoized);
